import React from 'react';
import { useRouteError, isRouteErrorResponse } from 'react-router-dom';
import { Box, Typography, Button } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';

/**
 * errorElement for react-router's data router.
 *
 * React Router's own default error boundary catches render/loader errors
 * before they can reach a React class ErrorBoundary higher in the tree
 * (e.g. the one wrapping <App />), so every route branch needs its own
 * errorElement to show a matching, on-brand fallback instead of the
 * router's built-in "Unexpected Application Error!" page.
 */
const RouterErrorBoundary = () => {
    const error = useRouteError();

    let message = 'The application encountered an unexpected error.';
    if (isRouteErrorResponse(error)) {
        message = `${error.status} ${error.statusText}`;
    } else if (error instanceof Error) {
        message = error.message;
    } else if (typeof error === 'string') {
        message = error;
    }

    return (
        <Box
            sx={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: '#0a0a0a',
                color: '#ff0000',
                fontFamily: '"Courier New", Courier, monospace',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '40px',
                zIndex: 9999,
            }}
        >
            <ErrorOutlineIcon
                sx={{
                    fontSize: '80px',
                    color: '#ff0000',
                    marginBottom: '20px',
                }}
            />

            <Typography
                sx={{
                    fontFamily: 'inherit',
                    fontSize: '24px',
                    color: '#ff0000',
                    marginBottom: '10px',
                    textAlign: 'center',
                }}
            >
                {'>'} CRITICAL ERROR
            </Typography>

            <Typography
                sx={{
                    fontFamily: 'inherit',
                    fontSize: '14px',
                    color: '#ff6666',
                    marginBottom: '30px',
                    textAlign: 'center',
                    maxWidth: '600px',
                }}
            >
                {message}
            </Typography>

            <Button
                onClick={() => window.location.reload()}
                startIcon={<RefreshIcon />}
                sx={{
                    color: '#00ff00',
                    border: '1px solid #00ff00',
                    fontFamily: 'inherit',
                    fontSize: '14px',
                    padding: '8px 20px',
                    '&:hover': {
                        backgroundColor: 'rgba(0, 255, 0, 0.2)',
                        borderColor: '#00ff00',
                    },
                }}
            >
                Reload Page
            </Button>
        </Box>
    );
};

export default RouterErrorBoundary;
